<script lang="ts">
	import { browser } from '$app/environment';
	import { CONTINENT_LABELS } from '$lib/api/velatlas/response.types';
	import { createLazyLoadAttachment } from '$lib/attachments/lazyLoad';
	import Breadcrumbs from '$lib/components/ui/Breadcrumbs.svelte';
	import Carousel from '$lib/components/ui/Carousel.svelte';
	import CityCard from '$lib/components/ui/CityCard.svelte';
	import CollectionCard from '$lib/components/ui/collection/CollectionCard.svelte';
	import CoordsDecoration from '$lib/components/ui/CoordsDecoration.svelte';
	import CountryCard from '$lib/components/ui/CountryCard.svelte';
	import type { MapPoint } from '$lib/components/ui/map/_other/mapPoint.type';
	import NativeText from '$lib/components/ui/NativeText.svelte';
	import type { ThumbnailCity } from '$lib/features/city/cityThumbnail.type';
	import type { CollectionPreview } from '$lib/features/collection/model/collectionPreview';
	import type { CountryPreview } from '$lib/features/country/countryPreview.type';
	import type { ThumbnailCountry } from '$lib/features/country/countryThumbnail.type';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import { listFormatByAnd } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Component } from 'svelte';
	import { fade } from 'svelte/transition';
	import IconEarth from '~icons/mdi/earth';
	import IconLanguage from '~icons/mdi/language';
	import IconPerson from '~icons/mdi/person';
	import type { Output } from './+page.server';

	const { data }: { data: { output: Output } } = $props();

	let loadMap = $state<boolean>(false);

	const country = $derived<CountryPreview>(data.output.country);
	const nearbyCountries = $derived<ThumbnailCountry[]>(data.output.nearbyCountries);
	const language = $derived<Language>(data.output.language);
	const cities = $derived<ThumbnailCity[]>(data.output.cities);
	const mapPoints = $derived<MapPoint[]>(data.output.mapPoints);
	let heroImage = $derived<CoverImage>(country.images[0]);
	let previousImage = $derived<CoverImage>(heroImage);
	let currentImage = $derived<CoverImage>(heroImage);
	let transitionKey = $derived<string>(heroImage.filename);
	const collections = $derived<CollectionPreview[]>(data.output.collections);
	const hasCollections = $derived<boolean>(collections.length > 0);
	const meta = $derived({
		title: `${country.englishName} Travel Guide - Cities & Places to Visit`,
		description: `Explore ${country.englishName}. Discover the best cities, culture, and travel tips for your next trip to ${country.englishName}, including ${listFormatByAnd(cities.slice(0, 3).map((item) => item.englishName))}.`,
		canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCountry({ countrySlug: country.slug }).href}`,
		imageUrl: UrlUtil.getFullImageUrl(heroImage)
	});
	const combinedJsonLd = $derived(data.output.jsonLdBreadcrumbs);

	const observeMapLoading = createLazyLoadAttachment(() => (loadMap = true), '300px');

	function scrollToSection(event: MouseEvent, targetId: string) {
		event.preventDefault();
		const element = document.getElementById(targetId);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
			history.pushState(null, '', `#${targetId}`);
		}
	}

	$effect(() => {
		if (heroImage.filename === currentImage.filename) return;
		previousImage = currentImage;
		currentImage = heroImage;
		transitionKey = heroImage.filename;
	});
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<div class="flex min-h-screen flex-col gap-9 overflow-x-hidden md:gap-12">
	<section class="relative flex min-h-[75vh] flex-col overflow-hidden bg-black py-17 lg:flex-row">
		{@render Hero()}
	</section>

	<div class="container mx-auto flex flex-col gap-9 px-4 md:gap-15 lg:px-0">
		<section>
			<header class="flex justify-between px-2">
				<h2 class="text-2xl">Map of Cities in {country.englishName}</h2>
				<span class="hidden text-sm font-medium text-base-content/70 sm:block"> Click a pin to explore </span>
			</header>
			<div class="divider"></div>
			{@render Map()}
		</section>

		<section id="cities" class="scroll-mt-17">
			<header class="flex flex-col items-end justify-between lg:flex-row lg:items-center">
				<h2 class="self-start text-2xl">Cities in {country.englishName}</h2>
				<span class="text-sm font-medium text-base-content/70"> {cities.length} results </span>
			</header>
			<div class="divider"></div>
			{@render Cities()}
		</section>

		{#if hasCollections}
			<section id="collections" class="scroll-mt-17">
				<header class="flex flex-col items-end justify-between lg:flex-row lg:items-center">
					<h2 class="text-2xl">Collections of places in {country.englishName}</h2>
					<span class="text-sm font-medium text-base-content/70"> {collections.length} results </span>
				</header>
				<div class="divider"></div>
				{@render Collections()}
			</section>
		{/if}

		<section>
			<header>
				<h2 class="text-2xl">Countries near {country.englishName}</h2>
			</header>
			<div class="divider"></div>
			{@render NearbyCountries()}
		</section>
	</div>
</div>

{#snippet Hero()}
	<div class="absolute inset-0 z-0">
		{@render Backdrop()}
	</div>

	<div
		class="relative z-10 container mx-auto flex w-full flex-col justify-center gap-6 px-4 py-12 text-white lg:flex-row lg:px-0"
	>
		<div class="flex flex-col items-center justify-center gap-6 lg:w-[45%]">
			<div>
				<Breadcrumbs classes="mb-4" items={data.output.breadcrumbs} textColour="white" />
				<div class="relative z-10 min-w-0 text-white">
					{@render ContentHalf()}
				</div>
			</div>
		</div>
		<div class="h-[45vh] w-full lg:h-125 lg:w-[55%]">
			<Carousel images={country.images} bind:activeImage={heroImage} />
		</div>
	</div>

	<CoordsDecoration coordinates={mapPoints.map((item) => item.coords)} classes="text-white/70" />

	{#snippet Backdrop()}
		<div class="absolute inset-0 overflow-hidden bg-black">
			<img
				class="absolute inset-0 h-full w-full scale-110 object-cover blur-lg select-none"
				src={UrlUtil.getFullImageUrl(previousImage)}
				alt={previousImage.altText}
				aria-hidden="true"
				width={previousImage.width}
				height={previousImage.height}
				decoding="async"
			/>

			{#key transitionKey}
				<img
					in:fade={{ duration: 400 }}
					out:fade={{ duration: 400 }}
					class="absolute inset-0 h-full w-full scale-110 object-cover blur-lg select-none"
					src={UrlUtil.getFullImageUrl(currentImage)}
					alt={currentImage.altText}
					aria-hidden="true"
					width={currentImage.width}
					height={currentImage.height}
					decoding="async"
				/>
			{/key}

			<div class="absolute inset-0 z-10 bg-black/70"></div>
		</div>
	{/snippet}

	{#snippet ContentHalf()}
		<h1 class="mb-2 font-serif text-5xl wrap-break-word lg:text-7xl">
			{country.englishName}
		</h1>
		{#if country.nativeName}
			<div class="mb-6 text-xs font-bold tracking-widest text-white/60">
				<NativeText languageCode={language.code} isLanguageRTL={language.rtl} text={country.nativeName.toUpperCase()} />
			</div>
		{/if}

		<div class="mb-8 max-w-lg border-l-2 border-white/20 pl-4 sm:pl-6">
			<h2 class="mb-2 text-xs font-bold tracking-widest text-white/60">
				{data.output.countryIntro.headline.toUpperCase()}
			</h2>
			<p class="text-base leading-relaxed font-light text-white/95 sm:text-lg">
				{data.output.countryIntro.body}
			</p>
		</div>

		<div class="flex flex-wrap gap-x-6 gap-y-6 text-xs leading-tight font-bold text-white">
			{@render StatItem(IconEarth, 'CONTINENT', CONTINENT_LABELS[country.continent])}
			{@render StatItem(IconPerson, 'POPULATION', country.population.toLocaleString())}
			{@render StatItem(IconLanguage, 'LANGUAGE', language.name)}
		</div>

		<div class="mt-8 flex flex-row items-center gap-2">
			{@render ScrollBtn(`${cities.length} Cities`, 'cities')}
			{#if hasCollections}
				{@render ScrollBtn(`${collections.length} Collections`, 'collections')}
			{/if}
		</div>

		{#snippet StatItem(Icon: Component, label: string, value: string)}
			<div class="flex flex-col gap-1">
				<div class="inline-flex items-center gap-2 tracking-widest text-white/60">
					<Icon class="text-sm" />
					{label}
				</div>
				<div class="text-base leading-tight sm:text-lg">{value}</div>
			</div>
		{/snippet}

		{#snippet ScrollBtn(label: string, sectionId: string)}
			<a
				class="btn border-white/60 btn-outline text-white btn-sm hover:bg-white hover:text-black"
				href={`#${sectionId}`}
				onclick={(e) => scrollToSection(e, sectionId)}
			>
				{label}
			</a>
		{/snippet}
	{/snippet}
{/snippet}

{#snippet Map()}
	<figure aria-label="Map of {country.englishName} with all the cities we have data on for this country">
		<div {@attach observeMapLoading} class="h-[60vh] w-full overflow-hidden rounded-box shadow-sm">
			{#if browser && loadMap}
				{#await import('$lib/components/ui/map/GenericMap.svelte') then { default: GenericMap }}
					<GenericMap points={mapPoints} />
				{/await}
			{:else}
				<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
					<span class="loading mr-2 loading-spinner text-primary"></span>
					<span>Loading map of {cities.length} cities...</span>
				</div>
			{/if}
		</div>
		<figcaption class="sr-only">
			Interactive map showing locations of {cities.length} cities in {country.englishName}, including {cities
				.slice(0, 5)
				.map((c) => c.englishName)
				.join(', ')}.
		</figcaption>
	</figure>
{/snippet}

{#snippet Cities()}
	<ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
		{#each cities as city, i (city.id)}
			<li>
				<CityCard {city} eagerLoadCoverImage={i < 6} />
			</li>
		{/each}
	</ul>
{/snippet}

{#snippet Collections()}
	<ul class="grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-4">
		{#each collections as collection, i (collection.metadata.slug)}
			<li>
				<CollectionCard
					{collection}
					nav={UrlUtil.toCountryCollection({ countrySlug: country.slug, collectionSlug: collection.metadata.slug })}
					index={i}
				/>
			</li>
		{/each}
	</ul>
{/snippet}

{#snippet NearbyCountries()}
	<ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
		{#each nearbyCountries as country (country.code)}
			<li>
				<CountryCard {country} eagerLoadCoverImage={true} />
			</li>
		{/each}
	</ul>
{/snippet}
